'use client';

import React from 'react';
import { ChatBotIcon } from '@terra/ui-icons';
import type {
  ConfirmationMessageType,
  DataMessageType,
} from '@terra/shared-dto-chat-types';
import { AssistantMessageCard } from './AssistantMessageCard';
import { DataMessageCard } from './DataMessageCard';
import { HandledDataMessageCard } from './HandledDataMessageCard';
import { MessageSkeletonCard } from './MessageSkeletonCard';
import { getMessageContentPart } from '../utils/getVercelMessageContentPart';
import type { AgentInfo } from '../types';
import type {
  ProcessedMessage,
  ProcessedPart,
} from '../utils/message-processing';
import { buildToolResultsById } from '../utils/message-processing';
import type { MessageListCallbacks } from './ChatMessageList';

export function renderChatMessagePart({
  processedPart,
  processedMessage,
  callbacks,
  noAssistantHeader,
  agent,
  autoCollapseToolCalls,
  isFinalAssistantOutcome,
  isChatLoading,
  nestedContent,
  nestedSummary,
  isNestedInSubAgent,
  forceExpanded,
  isGroupRunning,
  toolResultsById,
}: {
  processedPart: ProcessedPart;
  processedMessage: ProcessedMessage;
  callbacks: MessageListCallbacks;
  noAssistantHeader: boolean;
  agent?: AgentInfo;
  autoCollapseToolCalls: boolean;
  isFinalAssistantOutcome: boolean;
  isChatLoading: boolean;
  nestedContent?: React.ReactNode;
  nestedSummary?: string;
  isNestedInSubAgent?: boolean;
  forceExpanded?: boolean;
  isGroupRunning?: boolean;
  toolResultsById: ReturnType<typeof buildToolResultsById>;
}): React.ReactNode {
  switch (processedPart.kind) {
    case 'skip':
      return null;
    case 'handled-confirmation':
      return (
        <HandledDataMessageCard
          key={processedPart.key}
          messageWithSubs={processedPart.dataMessage as ConfirmationMessageType}
          isNestedInSubAgent={isNestedInSubAgent}
        />
      );
    case 'data-message':
      return (
        <DataMessageCard
          key={processedPart.key}
          uiMessage={processedMessage.message}
          dataMessage={processedPart.dataMessage as DataMessageType}
          messagePartId={processedPart.messagePartId}
          onDataMessageEvent={callbacks.onDataMessageEvent}
          onHITLActionMessageEvent={callbacks.onHITLActionMessageEvent}
          onAbortSubagent={callbacks.onAbortSubagent}
          focusedSubagentToolCallId={callbacks.focusedSubagentToolCallId}
          onOpenEditInMainContent={callbacks.onOpenEditInMainContent}
          onCloseEditInMainContent={callbacks.onCloseEditInMainContent}
          onOpenHttpInMainContent={callbacks.onOpenHttpInMainContent}
          onOpenNodeArgsInMainContent={callbacks.onOpenNodeArgsInMainContent}
          onOpenBrowserStreamInMainContent={
            callbacks.onOpenBrowserStreamInMainContent
          }
          onOpenPlanInMainContent={callbacks.onOpenPlanInMainContent}
          onBuildPlan={callbacks.onBuildPlan}
          onRevertCopilotModeSwitch={callbacks.onRevertCopilotModeSwitch}
          onConfirmCopilotModeSwitch={callbacks.onConfirmCopilotModeSwitch}
          currentCopilotMode={callbacks.currentCopilotMode}
          onOpenObject={callbacks.onOpenObject}
          skillDraftReference={callbacks.skillDraftReference}
          onSSESubscribe={callbacks.onSSESubscribe}
          isLastMessage={processedMessage.isLastMessage}
          isChatLoading={isChatLoading}
          hasToolResult={processedPart.hasToolResult}
          hideRequestIfShownInHITL={processedPart.wasShownInHITL}
          hitlResolvedToolResultInMessages={
            processedPart.hitlResolvedToolResultInMessages
          }
          autoCollapseToolCalls={autoCollapseToolCalls}
          selectedDecision={
            processedPart.selectedDecision ?? processedPart.hitlDecision
          }
          selectedDecisions={
            processedPart.selectedDecisions ?? processedPart.hitlDecisions
          }
          toolResultsById={toolResultsById}
          forceExpandedToolCall={forceExpanded}
          isGroupRunningToolCall={isGroupRunning}
          nestedContent={nestedContent}
          nestedSummary={nestedSummary}
          onEditAskUserAnswer={callbacks.onEditAskUserAnswer}
        />
      );
    case 'assistant-text':
      return (
        <AssistantMessageCard
          key={processedPart.key}
          message={processedPart.part}
          agent={agent}
          noHeader={noAssistantHeader}
          isFinalOutcome={isFinalAssistantOutcome}
          isStreaming={isChatLoading && processedMessage.isLastAssistantMessage}
          isNestedInSubAgent={isNestedInSubAgent}
          expansionKey={processedPart.key}
          toolResultsById={toolResultsById}
          onOpenObject={callbacks.onOpenObject}
        />
      );
    case 'skeleton': {
      const content = getMessageContentPart(processedPart.part, 'text');

      return (
        <MessageSkeletonCard
          key={processedPart.key}
          header={<ChatBotIcon />}
          markdownSource={content || ''}
        />
      );
    }

    default:
      return null;
  }
}
